import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'

interface Property {
    _id: string;
    propertyCategory?: string;
    subCategory?: string;
    propertyType: string;
    price: number;
    size?: number;
    buildingName?: string;
    area: string;
    city?: string;
    occupancyType?: string;
    propertyAge?: string;
    floor?: string;
    totalFloors?: string;
    washrooms?: number;
    bhkConfiguration?: string;
    furnishingType?: string;
    description?: string;
    totalUnit?: string;
    facing?: string;
    noOfParking?: string;
    contactName?: string;
    contactNumber?: string;
    contactEmail?: string;
    postedBy?: string;
}

export default function AdminEditClassifiedProperty() {
    const { token } = useAuth()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [form, setForm] = useState<Partial<Property>>({})
    const [images, setImages] = useState<File[]>([])
    const [existingImages, setExistingImages] = useState<string[]>([])

    useEffect(() => {
        if (!token || !id) return
        fetch(`${API_BASE}/classified-properties/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (data._id) {
                    setForm(data)
                    setExistingImages(data.images || [])
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token, id])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setImages(Array.from(e.target.files))
    }

    const removeExistingImage = (url: string) => {
        setExistingImages(p => p.filter(u => u !== url))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token || !id) return
        setSubmitting(true)

        let newImageUrls: string[] = []
        if (images.length > 0) {
            try {
                const uploadRes = await api.uploadImages(token, images)
                if (uploadRes.urls) newImageUrls = uploadRes.urls
            } catch (err) {
                toast.error('Image upload failed.')
                setSubmitting(false)
                return
            }
        }

        const finalImageUrls = [...existingImages, ...newImageUrls]

        try {
            const res = await fetch(`${API_BASE}/classified-properties/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ ...form, images: finalImageUrls })
            })

            if (res.ok) {
                toast.success('Property updated successfully')
                navigate('/admin-saved-properties')
            } else {
                toast.error('Failed to update property')
            }
        } catch (error) {
            console.error(error)
            toast.error('Error updating property')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return (
        <AdminLayout>
            <div className="p-20 text-center">
                <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#05144b] font-bold">Loading property details...</p>
            </div>
        </AdminLayout>
    )

    return (
        <AdminLayout>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-[#05144b]">Edit Classified Property</h1>
                    <p className="text-slate-500 text-sm">Update the imported property details below.</p>
                </div>
                <Link to="/admin-saved-properties" className="px-6 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all">
                    Cancel
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Category & Type */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Property Category*</label>
                            <input type="text" name="propertyCategory" value={form.propertyCategory || ''} onChange={handleChange} required className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b] focus:border-[#05144b]/20" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Sub Category*</label>
                            <input type="text" name="subCategory" value={form.subCategory || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Property Type*</label>
                            <input type="text" name="propertyType" value={form.propertyType || ''} onChange={handleChange} required className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>

                        {/* Price & Size */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Price*</label>
                            <input type="number" name="price" value={form.price || ''} onChange={handleChange} required className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Size (sqft)*</label>
                            <input type="number" name="size" value={form.size || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">BHK Configuration</label>
                            <input type="text" name="bhkConfiguration" value={form.bhkConfiguration || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>

                        {/* Location */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Building Name*</label>
                            <input type="text" name="buildingName" value={form.buildingName || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Area*</label>
                            <input type="text" name="area" value={form.area || ''} onChange={handleChange} required className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">City*</label>
                            <input type="text" name="city" value={form.city || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>

                        {/* Specs */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Occupancy Type</label>
                            <input type="text" name="occupancyType" value={form.occupancyType || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Property Age</label>
                            <input type="text" name="propertyAge" value={form.propertyAge || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Floor Num</label>
                            <input type="text" name="floor" value={form.floor || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Total Floors</label>
                            <input type="text" name="totalFloors" value={form.totalFloors || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>

                        {/* More Specs */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Washrooms</label>
                            <input type="number" name="washrooms" value={form.washrooms || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Furnishing Type</label>
                            <input type="text" name="furnishingType" value={form.furnishingType || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Total Units</label>
                            <input type="text" name="totalUnit" value={form.totalUnit || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>

                        {/* Orientation */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Facing</label>
                            <input type="text" name="facing" value={form.facing || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">No of Parking</label>
                            <input type="text" name="noOfParking" value={form.noOfParking || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Posted By</label>
                            <select name="postedBy" value={form.postedBy || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]">
                                <option value="Owner">Owner</option>
                                <option value="Broker">Broker</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-8 space-y-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Description*</label>
                        <textarea name="description" value={form.description || ''} onChange={handleChange} required rows={4} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b] focus:border-[#05144b]/20" placeholder="Enter property description..." />
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-12">
                    <div className="flex items-center gap-4 mb-8">
                        <i className="fa-solid fa-address-book text-2xl text-[#05144b]"></i>
                        <h3 className="text-xl font-bold text-[#05144b]">Imported Contact Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Contact Name</label>
                            <input type="text" name="contactName" value={form.contactName || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Contact Number</label>
                            <input type="text" name="contactNumber" value={form.contactNumber || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Contact Email</label>
                            <input type="email" name="contactEmail" value={form.contactEmail || ''} onChange={handleChange} className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-[#05144b]" />
                        </div>
                    </div>
                </div>

                {/* Media Card */}
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-12">
                    <div className="flex items-center gap-4 mb-8">
                        <i className="fa-solid fa-images text-2xl text-[#05144b]"></i>
                        <h3 className="text-xl font-bold text-[#05144b]">Property Media</h3>
                    </div>
                    
                    <div className="mb-6 flex flex-wrap gap-4">
                        {existingImages.map((url, i) => (
                            <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200">
                                <img src={getImageUrl(url)} className="w-full h-full object-cover" alt="Property" />
                                <button type="button" onClick={() => removeExistingImage(url)} className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors">
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        ))}
                        {images.map((file, i) => (
                            <div key={`new-${i}`} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-green-400">
                                <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="New Preview" />
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <span className="text-white text-[10px] font-bold uppercase tracking-widest bg-black/50 px-2 py-1 rounded-full">New</span>
                                </div>
                                <button type="button" onClick={() => setImages(p => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors">
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors">
                        <i className="fa-solid fa-cloud-arrow-up text-4xl text-slate-400 mb-4"></i>
                        <h5 className="text-sm font-bold text-[#05144b] mb-1">Upload New Images</h5>
                        <p className="text-xs text-slate-500 mb-4">Select additional images for this property</p>
                        <input type="file" className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-[#05144b] file:text-white hover:file:bg-[#05144b]/90 cursor-pointer mx-auto max-w-sm" multiple onChange={handleImageChange} accept="image/*" />
                        {images.length > 0 && <p className="mt-4 text-green-600 text-xs font-bold">{images.length} new images selected</p>}
                    </div>
                </div>

                <div className="flex justify-end">
                    <button type="submit" disabled={submitting} className="px-12 py-4 bg-[#05144b] text-white rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-opacity-90 transition-all shadow-xl shadow-blue-900/10 disabled:opacity-50">
                        {submitting ? 'Updating...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </AdminLayout>
    )
}
